import { motion } from 'framer-motion';
import { MessageSquare, BarChart3, Zap, Globe, Users, Shield } from 'lucide-react';
import { useLang } from '../../../context/LangContext';

const features = [
  {
    icon: MessageSquare,
    titleKey: "features.1.title",
    descKey: "features.1.desc",
    color: "brand-green"
  },
  {
    icon: Users,
    titleKey: "features.4.title",
    descKey: "features.4.desc",
    color: "brand-blue"
  },
  {
    icon: BarChart3,
    titleKey: "features.6.title",
    descKey: "features.6.desc",
    color: "brand-gold"
  },
  {
    icon: Zap,
    titleKey: "features.5.title",
    descKey: "features.5.desc",
    color: "brand-green"
  },
  {
    icon: Globe,
    titleKey: "features.2.title",
    descKey: "features.2.desc",
    color: "brand-blue"
  },
  {
    icon: Shield,
    titleKey: "features.3.title",
    descKey: "features.3.desc",
    color: "brand-gold"
  }
];

export const AgentFeatures = () => {
  const { t } = useLang();

  return (
    <section data-testid="agent-features" className="py-24 bg-[#020209]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t("features.title")}
          </h2>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="group relative p-6 rounded-2xl bg-white/[0.02] border border-white/[0.05] hover:border-white/[0.1] transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-${feature.color}/10 text-${feature.color} mb-4`}>
                <feature.icon size={24} />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t(feature.titleKey)}
              </h3>
              <p className="text-neutral-400 text-sm leading-relaxed">
                {t(feature.descKey)}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Anatomie Section */}
        <motion.div 
          className="mt-24 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t("agent.anatomie.title")}
          </h2>
          <p className="text-neutral-400 text-lg max-w-2xl mx-auto mb-16">
            {t("agent.anatomie.subtitle")}
          </p>
        </motion.div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[1, 2, 3].map((step, index) => (
            <motion.div
              key={step}
              className="relative p-8 rounded-2xl bg-gradient-to-br from-white/[0.03] to-transparent border border-white/[0.05]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.15 }}
            >
              <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-brand-green flex items-center justify-center text-white font-bold text-sm">
                {step}
              </div>
              <span className="inline-block px-3 py-1 rounded-full bg-brand-gold/10 text-brand-gold text-xs font-medium mb-4">
                {t(`agent.step${step}.badge`)}
              </span>
              <h3 className="text-xl font-semibold text-white mb-3">
                {t(`agent.step${step}.title`)}
              </h3>
              <p className="text-neutral-400 text-sm leading-relaxed">
                {t(`agent.step${step}.desc`)}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
